import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import {
  api,
  ApiError,
  type RulesExample,
  type RulesStage,
  type RulesWorkedPrediction,
} from '../api.js';
import AppHeader from '../components/AppHeader.js';
import PoolNav from '../components/PoolNav.js';
import CustomRulesScreen from './CustomRulesScreen.js';
import styles from './RulesScreen.module.css';

/**
 * Ported from the legacy `.reg-*` rules page in spirit, not in literal
 * content: that page hand-typed its numbers and worked examples straight
 * into HTML, so a scoring-parameter change silently made the printed page
 * a lie. Every number here — including the worked examples — comes from
 * `GET /groups/:id/rules`, which resolves the group's own ruleset and
 * scores the examples with the same engine that scores real matches. See
 * PLAN.md 5.8.
 */

function ScoreBadge({
  home,
  away,
  penaltyWinner,
}: {
  home: number;
  away: number;
  penaltyWinner: 'home' | 'away' | null;
}) {
  const { t } = useTranslation();
  return (
    <span className={styles.exampleBet}>
      {t('rules.predictedScore', { home, away })}
      {penaltyWinner ? (
        <span className={styles.penaltyNote}>
          {' '}
          ({penaltyWinner === 'home' ? t('rules.penaltyHome') : t('rules.penaltyAway')})
        </span>
      ) : null}
    </span>
  );
}

function PointBadges({ points }: { points: RulesWorkedPrediction['points'] }) {
  const { t } = useTranslation();
  return (
    <span className={styles.pointBadges}>
      {points.outcome > 0 ? (
        <span className={`${styles.ptBadge} ${styles.swatchWinner}`}>{points.outcome}</span>
      ) : null}
      {points.exact > 0 ? (
        <span className={`${styles.ptBadge} ${styles.swatchExact}`}>{points.exact}</span>
      ) : null}
      {points.proximity > 0 ? (
        <span className={`${styles.ptBadge} ${styles.swatchProx}`}>{points.proximity}</span>
      ) : null}
      {points.penalty > 0 ? (
        <span className={`${styles.ptBadge} ${styles.swatchPenalty}`}>{points.penalty}</span>
      ) : null}
      <span className={styles.ptsBadge}>
        {t('predictions.revealedPoints', { count: points.total })}
      </span>
    </span>
  );
}

function ExampleRow({
  prediction,
  narrative,
  variant,
}: {
  prediction: RulesWorkedPrediction;
  narrative: string;
  variant: 'perfect' | 'good' | 'ok' | 'bad';
}) {
  return (
    <div className={`${styles.exampleItem} ${styles[variant]}`}>
      <div className={styles.exampleBetRow}>
        <ScoreBadge
          home={prediction.home}
          away={prediction.away}
          penaltyWinner={prediction.penaltyWinner}
        />
        <PointBadges points={prediction.points} />
      </div>
      <span className={styles.exampleResult}>{narrative}</span>
    </div>
  );
}

function TechToggle({ label, children }: { label: string; children: React.ReactNode }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <button
        type="button"
        className={styles.techToggle}
        aria-expanded={expanded}
        onClick={() => setExpanded((e) => !e)}
      >
        <span>{label}</span>
        <span className={styles.techChevron}>{expanded ? '▲' : '▼'}</span>
      </button>
      <div className={`${styles.techContent} ${expanded ? styles.techContentExpanded : ''}`}>
        {children}
      </div>
    </div>
  );
}

function GroupSection({ example }: { example: RulesExample }) {
  const { t } = useTranslation();
  const [exact, close, wrongWinner] = example.predictions;
  const { params } = example;

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionIcon}>⚽</span>
        <h2>{t('rules.groupSection.title')}</h2>
      </div>
      <p className={styles.body}>{t('rules.groupSection.body')}</p>

      <div className={styles.pointsGrid}>
        <div className={styles.pointCard}>
          <span className={styles.pointEmoji}>✅</span>
          <span className={styles.pointLabel}>{t('rules.groupSection.cardResult')}</span>
          <span className={styles.pointValue}>
            {t('rules.pointsShort', { count: params.outcome })}
          </span>
        </div>
        <div className={styles.pointCard}>
          <span className={styles.pointEmoji}>🎯</span>
          <span className={styles.pointLabel}>{t('rules.groupSection.cardExact')}</span>
          <span className={styles.pointValue}>
            {t('rules.pointsShort', { count: params.exact })}
          </span>
          <span className={styles.pointNote}>{t('rules.groupSection.cardExactNote')}</span>
        </div>
        <div className={`${styles.pointCard} ${styles.pointCardWide}`}>
          <span className={styles.pointEmoji}>🍕</span>
          <span className={styles.pointLabel}>{t('rules.groupSection.cardPot')}</span>
          <span className={styles.pointValue}>{t('rules.pointsShort', { count: params.pot })}</span>
          <span className={styles.pointNote}>{t('rules.groupSection.cardPotNote')}</span>
        </div>
      </div>

      <div className={styles.exampleBox}>
        <div className={styles.exampleTitle}>{t('rules.exampleTitle')}</div>
        <p className={styles.exampleContext}>
          {t('rules.groupSection.exampleContext', {
            home: example.real.home,
            away: example.real.away,
          })}
        </p>
        {exact ? (
          <ExampleRow
            prediction={exact}
            narrative={t('rules.groupSection.narrativeExact')}
            variant="perfect"
          />
        ) : null}
        {close ? (
          <ExampleRow
            prediction={close}
            narrative={t('rules.groupSection.narrativeClose')}
            variant="good"
          />
        ) : null}
        {wrongWinner ? (
          <ExampleRow
            prediction={wrongWinner}
            narrative={t('rules.groupSection.narrativeWrongWinner')}
            variant="bad"
          />
        ) : null}
      </div>

      <TechToggle label={t('rules.techToggle')}>
        <p className={styles.paramLegend}>
          {t('rules.groupSection.techParams', {
            outcome: params.outcome,
            exact: params.exact,
            pot: params.pot,
            decay: params.decay,
            threshold: params.threshold,
          })}
        </p>
        <p>{t('rules.groupSection.techNote')}</p>
      </TechToggle>
    </section>
  );
}

function KnockoutSection({ example, stageName }: { example: RulesExample; stageName: string }) {
  const { t } = useTranslation();
  const [rightWinnerWrongScore, drawWrongPenalty, perfect] = example.predictions;
  const { params } = example;

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionIcon}>🔥</span>
        <h2>{t('rules.knockoutSection.title')}</h2>
      </div>
      <p className={styles.body}>{t('rules.knockoutSection.body')}</p>
      <p className={styles.bodyNote}>
        {t('rules.knockoutSection.stageNote', { stage: stageName })}
      </p>

      <div className={styles.pointsGrid}>
        <div className={styles.pointCard}>
          <span className={styles.pointEmoji}>✅</span>
          <span className={styles.pointLabel}>{t('rules.knockoutSection.cardResult')}</span>
          <span className={styles.pointValue}>
            {t('rules.pointsShort', { count: params.outcome })}
          </span>
        </div>
        <div className={styles.pointCard}>
          <span className={styles.pointEmoji}>🎯</span>
          <span className={styles.pointLabel}>{t('rules.knockoutSection.cardExact')}</span>
          <span className={styles.pointValue}>
            {t('rules.pointsShort', { count: params.exact })}
          </span>
        </div>
        <div className={styles.pointCard}>
          <span className={styles.pointEmoji}>🍕</span>
          <span className={styles.pointLabel}>{t('rules.knockoutSection.cardPot')}</span>
          <span className={styles.pointValue}>{t('rules.pointsShort', { count: params.pot })}</span>
        </div>
        <div className={styles.pointCard}>
          <span className={styles.pointEmoji}>⚽</span>
          <span className={styles.pointLabel}>{t('rules.knockoutSection.cardPenaltyExact')}</span>
          <span className={styles.pointValue}>
            {t('rules.pointsShort', { count: params.penaltyExact })}
          </span>
        </div>
        <div className={styles.pointCard}>
          <span className={styles.pointEmoji}>🤏</span>
          <span className={styles.pointLabel}>{t('rules.knockoutSection.cardPenaltyWrong')}</span>
          <span className={styles.pointValue}>
            {t('rules.pointsShort', { count: params.penaltyWrong })}
          </span>
        </div>
      </div>

      <div className={styles.callout}>
        <p>{t('rules.knockoutSection.calloutSplit', { outcome: params.outcome })}</p>
        <p className={styles.calloutNote}>{t('rules.knockoutSection.calloutRounding')}</p>
      </div>

      <div className={styles.exampleBox}>
        <div className={styles.exampleTitle}>{t('rules.exampleTitle')}</div>
        <p className={styles.exampleContext}>
          {t('rules.knockoutSection.exampleContext', {
            home: example.real.home,
            away: example.real.away,
          })}
          {example.real.penaltyWinner === 'home' ? ` (${t('rules.penaltyHome')})` : null}
          {example.real.penaltyWinner === 'away' ? ` (${t('rules.penaltyAway')})` : null}
        </p>
        {rightWinnerWrongScore ? (
          <ExampleRow
            prediction={rightWinnerWrongScore}
            narrative={t('rules.knockoutSection.narrativeRightWinnerWrongScore')}
            variant="good"
          />
        ) : null}
        {drawWrongPenalty ? (
          <ExampleRow
            prediction={drawWrongPenalty}
            narrative={t('rules.knockoutSection.narrativeDrawWrongPenalty')}
            variant="ok"
          />
        ) : null}
        {perfect ? (
          <ExampleRow
            prediction={perfect}
            narrative={t('rules.knockoutSection.narrativePerfect')}
            variant="perfect"
          />
        ) : null}
      </div>

      <TechToggle label={t('rules.techToggle')}>
        <p className={styles.paramLegend}>
          {t('rules.knockoutSection.techParams', {
            stage: stageName,
            outcome: params.outcome,
            exact: params.exact,
            pot: params.pot,
            decay: params.decay,
            threshold: params.threshold,
            penaltyExact: params.penaltyExact,
            penaltyWrong: params.penaltyWrong,
          })}
        </p>
        <p className={styles.formulaTitle}>{t('rules.knockoutSection.formulaTitle')}</p>
        <div className={styles.formulaBlock}>
          <code>{t('rules.knockoutSection.formula')}</code>
        </div>
        <p>{t('rules.knockoutSection.formulaNote')}</p>
      </TechToggle>
    </section>
  );
}

function ParamsSection({ stages }: { stages: RulesStage[] }) {
  const { t } = useTranslation();
  const decay = stages[0]?.params.decay;
  const threshold = stages[0]?.params.threshold;

  return (
    <section className={`${styles.section} ${styles.paramsSection}`}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionIcon}>📋</span>
        <h2>{t('rules.paramsSection.title')}</h2>
        <span className={styles.sectionBadge}>{t('rules.paramsSection.badge')}</span>
      </div>
      <p className={styles.body}>{t('rules.paramsSection.body')}</p>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t('rules.paramsSection.thStage')}</th>
              <th>{t('rules.paramsSection.thResult')}</th>
              <th>{t('rules.paramsSection.thExact')}</th>
              <th>{t('rules.paramsSection.thPot')}</th>
              <th>{t('rules.paramsSection.thPenaltyExact')}</th>
              <th>{t('rules.paramsSection.thPenaltyWrong')}</th>
            </tr>
          </thead>
          <tbody>
            {stages.map((stage) => (
              <tr
                key={stage.slug}
                className={
                  stage.slug === 'third-place'
                    ? styles.rowDimmed
                    : stage.slug === 'final'
                      ? styles.rowFinal
                      : undefined
                }
              >
                <td className={styles.phaseName}>
                  {stage.name}
                  {stage.slug === 'third-place' ? (
                    <span className={styles.asterisk}> *</span>
                  ) : null}
                  {stage.slug === 'final' ? ' 🏆' : null}
                </td>
                <td>{stage.params.outcome}</td>
                <td>{stage.params.exact}</td>
                <td>{stage.params.pot}</td>
                <td className={stage.canGoToPenalties ? undefined : styles.cellNa}>
                  {stage.canGoToPenalties ? stage.params.penaltyExact : '—'}
                </td>
                <td className={stage.canGoToPenalties ? undefined : styles.cellNa}>
                  {stage.canGoToPenalties ? stage.params.penaltyWrong : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {stages.some((s) => s.slug === 'third-place') ? (
        <div className={`${styles.callout} ${styles.calloutDimmed}`}>
          <p>{t('rules.paramsSection.thirdPlaceNote')}</p>
        </div>
      ) : null}

      {decay !== undefined && threshold !== undefined ? (
        <p className={styles.constants}>
          {t('rules.paramsSection.constants', { decay, threshold })}
        </p>
      ) : null}
    </section>
  );
}

function ProximitySection({ example }: { example: RulesExample }) {
  const { t } = useTranslation();
  const [exact, good, ok, bad] = example.predictions;
  const { params } = example;

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionIcon}>📊</span>
        <h2>{t('rules.proximitySection.title')}</h2>
        <span className={styles.sectionBadge}>{t('rules.proximitySection.badge')}</span>
      </div>
      <p className={styles.body}>{t('rules.proximitySection.body')}</p>

      <div className={styles.rulesList}>
        <div className={styles.rule}>
          <span className={styles.ruleIcon}>{t('rules.proximitySection.ruleWinnerIcon')}</span>
          <span>{t('rules.proximitySection.ruleWinner')}</span>
        </div>
        <div className={styles.rule}>
          <span className={styles.ruleIcon}>{t('rules.proximitySection.rulePotIcon')}</span>
          <span>{t('rules.proximitySection.rulePot')}</span>
        </div>
      </div>

      <div className={`${styles.callout} ${styles.calloutInfo}`}>
        <p>{t('rules.proximitySection.calloutInfo1')}</p>
        <p>{t('rules.proximitySection.calloutInfo2')}</p>
      </div>

      <div className={styles.exampleBox}>
        <div className={styles.exampleTitle}>{t('rules.exampleTitle')}</div>
        <p className={styles.exampleContext}>
          {t('rules.proximitySection.exampleContext', {
            home: example.real.home,
            away: example.real.away,
          })}
        </p>
        {exact ? (
          <ExampleRow
            prediction={exact}
            narrative={t('rules.proximitySection.narrativeExact')}
            variant="perfect"
          />
        ) : null}
        {good ? (
          <ExampleRow
            prediction={good}
            narrative={t('rules.proximitySection.narrativeGood')}
            variant="good"
          />
        ) : null}
        {ok ? (
          <ExampleRow
            prediction={ok}
            narrative={t('rules.proximitySection.narrativeOk')}
            variant="ok"
          />
        ) : null}
        {bad ? (
          <ExampleRow
            prediction={bad}
            narrative={t('rules.proximitySection.narrativeBad')}
            variant="bad"
          />
        ) : null}
      </div>

      <TechToggle label={t('rules.techToggle')}>
        <p>{t('rules.proximitySection.techIntro')}</p>

        <p className={styles.formulaTitle}>{t('rules.proximitySection.step1Title')}</p>
        <p>{t('rules.proximitySection.step1')}</p>
        <div className={styles.formulaBlock}>
          <code>{t('rules.proximitySection.step1Formula')}</code>
        </div>

        <p className={styles.formulaTitle}>{t('rules.proximitySection.step2Title')}</p>
        <div className={styles.formulaBlock}>
          <code>{t('rules.proximitySection.step2Formula')}</code>
        </div>

        <p className={styles.formulaTitle}>{t('rules.proximitySection.step3Title')}</p>
        <p>{t('rules.proximitySection.step3', { threshold: params.threshold })}</p>
        <div className={styles.formulaBlock}>
          <code>{t('rules.proximitySection.step3Formula')}</code>
        </div>

        <p className={styles.formulaTitle}>{t('rules.proximitySection.step4Title')}</p>
        <p>{t('rules.proximitySection.step4', { decay: params.decay })}</p>
        <div className={styles.formulaBlock}>
          <code>{t('rules.proximitySection.step4Formula', { decay: params.decay })}</code>
        </div>
      </TechToggle>
    </section>
  );
}

export default function RulesScreen() {
  const { t, i18n } = useTranslation();
  const { id = '', poolId = '' } = useParams();
  const locale = i18n.resolvedLanguage ?? 'en';

  const {
    data: pool,
    isPending: poolPending,
    isError: poolIsError,
  } = useQuery({ queryKey: ['pool', id, poolId], queryFn: () => api.getPool(id, poolId) });

  // A custom bolão has no single ruleset for `getRules` to resolve — its
  // rules page is a static explanation instead (CustomRulesScreen), so this
  // query never runs for one.
  const isCustom = pool?.sportId === 'custom';

  const {
    data: rules,
    isPending: rulesPending,
    isError: rulesIsError,
    error: rulesError,
  } = useQuery({
    queryKey: ['rules', id, poolId, locale],
    queryFn: () => api.getRules(id, poolId, locale),
    enabled: pool !== undefined && !isCustom,
  });

  if (poolPending || (!isCustom && rulesPending)) {
    return (
      <main className="screen">
        <p className="muted">{t('common.loading')}</p>
      </main>
    );
  }

  if (poolIsError || (!isCustom && rulesIsError)) {
    return (
      <main className="screen">
        <PoolNav groupId={id} poolId={poolId} />
        <p className="error">
          {rulesError instanceof ApiError ? rulesError.message : t('common.somethingWentWrong')}
        </p>
      </main>
    );
  }

  if (isCustom) {
    return (
      <main className="screen">
        <AppHeader groupId={id} poolId={poolId} />
        <PoolNav groupId={id} poolId={poolId} />
        <CustomRulesScreen />
      </main>
    );
  }

  // `enabled` guarantees `rules` is populated once loading/error are ruled
  // out above.
  if (!rules) return null;

  return (
    <main className="screen">
      <AppHeader groupId={id} poolId={poolId} />
      <PoolNav groupId={id} poolId={poolId} />

      <div className={styles.hero}>
        <span className={styles.heroIcon}>🏆</span>
        <h1 className={styles.heroTitle}>{t('rules.heroTitle')}</h1>
        {pool ? <p className="muted">{pool.name}</p> : null}
        <p className={styles.heroSubtitle}>{t('rules.heroSubtitle')}</p>
      </div>

      <GroupSection example={rules.groupStageExample} />
      <KnockoutSection
        example={rules.knockoutExample}
        stageName={
          rules.stages.find((s) => s.slug === rules.knockoutExample.stageSlug)?.name ??
          rules.knockoutExample.stageSlug
        }
      />
      <ParamsSection stages={rules.stages} />
      <ProximitySection example={rules.proximityExample} />

      <p className={styles.footer}>{t('rules.footer')}</p>
    </main>
  );
}
